import { WrappedData, Slide } from './types.js';
import { StatsCalculator } from './stats-calculator.js';

export class TeamSlideGenerator {
  constructor(private data: WrappedData, private statsCalculator: StatsCalculator) {}

  generateTeamSlides(): Slide[] {
    const slides: Slide[] = [];

    // Welcome slide
    slides.push(this.createWelcomeSlide());

    // Team summary
    slides.push(this.createTeamSummarySlide());

    // Most active days
    slides.push(this.createMostActiveDaysSlide());

    // Busiest months
    slides.push(this.createBusiestMonthsSlide());

    // Day of week distribution
    slides.push(this.createDayOfWeekSlide());

    // Pull requests if available
    if (this.data.pullRequests && this.data.pullRequests.totalPRs.merged > 0) {
      slides.push(this.createPRStatsSlide());
    }

    // Fun facts
    slides.push(this.createFunFactsSlide());

    return slides;
  }

  private createWelcomeSlide(): Slide {
    const period = this.data.period;
    return {
      title: 'Git Wrapped de l\'Équipe',
      emoji: '🎉',
      subtitle: 'Votre année en code, visualisée',
      content: `
        <div class="welcome">
          <h1>Git Wrapped de l'Équipe</h1>
          <p class="subtitle">${period.start} à ${period.end}</p>
          <p class="badge">Célébrons les réalisations de votre équipe!</p>
        </div>
      `
    };
  }

  private createTeamSummarySlide(): Slide {
    const summary = this.data.teamSummary;
    
    // Calculate actual days in period
    const startDate = new Date(this.data.period.start);
    const endDate = new Date(this.data.period.end);
    const daysDiff = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
    const avgCommitsPerDay = (summary.totalCommits / daysDiff).toFixed(1);
    
    return {
      title: 'Réalisations de l\'Équipe',
      emoji: '🚀',
      subtitle: 'Les grands chiffres de votre équipe',
      content: `
        <div class="stats-grid">
          <div class="stat">
            <div class="number">${summary.totalCommits.toLocaleString()}</div>
            <div class="label">commits totaux</div>
          </div>
          <div class="stat">
            <div class="number">${summary.linesAdded.toLocaleString()}</div>
            <div class="label">lignes ajoutées</div>
          </div>
          <div class="stat">
            <div class="number">${summary.linesDeleted.toLocaleString()}</div>
            <div class="label">lignes supprimées</div>
          </div>
          <div class="stat">
            <div class="number">${avgCommitsPerDay}</div>
            <div class="label">commits par jour</div>
          </div>
        </div>
      `
    };
  }

  private createBusiestMonthsSlide(): Slide {
    const busiestMonths = this.data.busiestMonths
      .sort((a, b) => b.commits - a.commits)
      .slice(0, 5);

    return {
      title: 'Mois les Plus Actifs',
      emoji: '📅',
      subtitle: 'Quand votre équipe était la plus productive',
      content: `
        <div class="leaderboard">
          ${busiestMonths.map(month => {
            const date = new Date(month.month + '-01');
            const monthName = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            return `
              <div class="leaderboard-item">
                <span class="name">${monthName}</span>
                <span class="value">${month.commits.toLocaleString()} commits</span>
              </div>
            `;
          }).join('')}
        </div>
      `
    };
  }

  private createPRStatsSlide(): Slide {
    const prData = this.data.pullRequests!;

    return {
      title: 'Pull Requests',
      emoji: '✅',
      subtitle: 'Stats de collaboration de l\'équipe',
      content: `
        <div class="stats-grid">
          <div class="stat">
            <div class="number">${prData.totalPRs.opened}</div>
            <div class="label">PRs ouvertes</div>
          </div>
          <div class="stat">
            <div class="number">${prData.totalPRs.merged}</div>
            <div class="label">PRs mergées</div>
          </div>
        </div>
        <p class="stat-label" style="margin-top: 2rem;">Excellente collaboration!</p>
      `
    };
  }

  private createMostActiveDaysSlide(): Slide {
    const busiestDays = this.data.busiestDays || [];
    const topDays = busiestDays.slice(0, 5);

    return {
      title: 'Jours les Plus Productifs',
      emoji: '🔥',
      subtitle: 'Jours avec le plus de commits',
      content: `
        <div class="leaderboard">
          ${topDays.map((day, index) => {
            const date = new Date(day.date);
            const formatted = date.toLocaleDateString('fr-FR', { 
              weekday: 'short',
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
            return `
              <div class="leaderboard-item">
                <span class="rank">${index + 1}.</span>
                <span class="name">${formatted}</span>
                <span class="value">${day.commits} commits</span>
              </div>
            `;
          }).join('')}
        </div>
      `
    };
  }

  private createDayOfWeekSlide(): Slide {
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayData = this.data.commitsByDayOfWeek || {};
    
    const sortedDays = dayOrder
      .map(day => ({ day, commits: dayData[day] || 0 }))
      .filter(d => d.commits > 0)
      .sort((a, b) => b.commits - a.commits);

    const total = Object.values(dayData).reduce((sum, count) => sum + count, 0);
    const mostPopularDay = sortedDays[0];
    const percentage = ((mostPopularDay.commits / total) * 100).toFixed(1);
    
    // French day names
    const frenchDays: Record<string, string> = {
      'Monday': 'Lundi',
      'Tuesday': 'Mardi',
      'Wednesday': 'Mercredi',
      'Thursday': 'Jeudi',
      'Friday': 'Vendredi',
      'Saturday': 'Samedi',
      'Sunday': 'Dimanche'
    };

    return {
      title: 'Jour Préféré pour Coder',
      emoji: '📅',
      subtitle: 'Quel jour de la semaine votre équipe préfère',
      content: `
        <div class="stat-large">${frenchDays[mostPopularDay.day]}</div>
        <p class="stat-label">${percentage}% de tous les commits (${mostPopularDay.commits.toLocaleString()} commits)</p>
        <div class="stats-grid" style="margin-top: 2rem;">
          ${sortedDays.slice(0, 5).map(d => `
            <div class="stat-card">
              <div class="stat-card-value">${d.commits}</div>
              <div class="stat-card-label">${frenchDays[d.day]}</div>
            </div>
          `).join('')}
        </div>
      `
    };
  }

  private createFunFactsSlide(): Slide {
    const totalCommits = this.data.teamSummary.totalCommits;
    
    // Calculate actual days in period
    const startDate = new Date(this.data.period.start);
    const endDate = new Date(this.data.period.end);
    const daysDiff = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
    const avgCommitsPerDay = (totalCommits / daysDiff).toFixed(1);
    
    // Most common day of week
    const dayData = this.data.commitsByDayOfWeek || {};
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const sortedDays = dayOrder
      .map(day => ({ day, commits: dayData[day] || 0 }))
      .sort((a, b) => b.commits - a.commits);
    
    const frenchDays: Record<string, string> = {
      'Monday': 'Lundi',
      'Tuesday': 'Mardi',
      'Wednesday': 'Mercredi',
      'Thursday': 'Jeudi',
      'Friday': 'Vendredi',
      'Saturday': 'Samedi',
      'Sunday': 'Dimanche'
    };
    
    const favoriteDay = sortedDays[0]?.day ? frenchDays[sortedDays[0].day] : 'N/A';

    return {
      title: 'Faits Amusants',
      emoji: '🎯',
      subtitle: 'Chiffres intéressants de votre équipe',
      content: `
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-card-value">${avgCommitsPerDay}</div>
            <div class="stat-card-label">Commits par jour</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">${this.data.teamSummary.linesAdded.toLocaleString()}</div>
            <div class="stat-card-label">Lignes ajoutées</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">${this.data.teamSummary.linesDeleted.toLocaleString()}</div>
            <div class="stat-card-label">Lignes supprimées</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">${favoriteDay}</div>
            <div class="stat-card-label">Jour de codage préféré</div>
          </div>
        </div>
        <div style="margin-top: 2rem;">
          <p class="stat-label">
            🎉 Ensemble, votre équipe a livré ${totalCommits.toLocaleString()} commits cette année!
          </p>
        </div>
      `
    };
  }
}
